import { useCallback } from "react";

import { Typography } from "antd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { RVIcon, RVIconSizeOptions } from "@regal-voice/ui";
import { AppPagePaths } from "App/routes/paths";
import { orchestrationPathResources } from "Pages/orchestration/Pages/orchestrationPaths";

import { JourneyVersionsList } from "./components/JourneyVersionsList/JourneyVersionsList";
import { useGetJourneyVersions } from "./hooks/useGetJourneyVersions";

import styles from "./JourneyVersionsPanel.module.scss";

export type JourneyVersionsRouterParams = { journeyId: string };

export type JourneyVersionsPanelProps = {
    onClose: () => void;
};

export function JourneyVersionsPanel({ onClose }: JourneyVersionsPanelProps): JSX.Element {
    const routeParams = useParams<JourneyVersionsRouterParams>();
    const journeyId = routeParams.journeyId as string;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { loading, data } = useGetJourneyVersions(journeyId);

    const viewingVersion = searchParams.get("version");
    const currentVersionString = viewingVersion || data.find((version) => version.isActive)?.version?.toString() || "";

    const handleClose = useCallback(() => {
        // When closing, always navigate back to the active version (remove version parameter)
        if (viewingVersion) {
            // Navigate back to the active version; React/Apollo will handle data refresh
            navigate(`${AppPagePaths.orchestration}/${orchestrationPathResources.journeys}/${journeyId}`, {
                replace: true,
            });
            onClose();
        } else {
            onClose();
        }
    }, [journeyId, navigate, onClose, viewingVersion]);

    return (
        <>
            <div className={styles.header}>
                <Typography.Title level={4} className={styles.journeyVersionsTitle}>
                    {"Journey Version History"}
                </Typography.Title>
                <RVIcon
                    icon={"close"}
                    data-testid="close-versions-icon"
                    size={RVIconSizeOptions.SMALL}
                    className={styles.closeIcon}
                    onClick={handleClose}
                />
            </div>
            <div className={styles.versionsContainer}>
                <JourneyVersionsList
                    versionList={data}
                    loading={loading}
                    journeyId={journeyId}
                    currentVersion={currentVersionString}
                    onClose={onClose}
                />
            </div>
        </>
    );
}
